/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION, WATCH_STATUS_ON, WATCH_STATUS_OPEN, ALL, WATCH_STATUS_CALLS_IN, WATCH_TYPE_CALL, WATCH_TYPE_SMS } from '../../Constants';
import { WATCH_STATUS_CALLS_OUT, WATCH_STATUS_CLOSED } from '../../Constants';
import cookie from "js-cookie";
import { useTranslation } from 'react-i18next';
import { handleApiErrorMessage, getCommunityGeneralInfo } from "../../Helper"
import { useDispatch } from "react-redux";
import { showErrorDialog } from "../../features/errorDialog";
import '../../styles.css';
import Switch from "../Switch";
import Select from "../Select";
import Chip from "../Chip";
import WatchSystemDesktop from "./Desktop";
import WatchSystemMobile from "./Mobile";
import { Backdrop, CircularProgress} from '@mui/material';

export default function WatchSystem(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [watchStatus, setWatchStatus] = useState(WATCH_STATUS_ON);
  const [watchType, setWatchType] = useState(WATCH_TYPE_CALL);
  const [displayWatchTypeSwitch, setDisplayWatchTypeSwitch] = useState(false);

  let initialWatchProfile = {};
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    initialWatchProfile['' + dayIndex] = {};
    for (let hourIndex = 0; hourIndex < 24; hourIndex++) {
      initialWatchProfile['' + dayIndex]['' + hourIndex] = {};
      initialWatchProfile['' + dayIndex]['' + hourIndex]['status'] = WATCH_STATUS_OPEN;
      initialWatchProfile['' + dayIndex]['' + hourIndex]['calls'] = ALL;
    }
  }
  const [watchProfile, setWatchProfile] = useState(null);
  const [excludeNumbers, setExcludeNumbers] = useState([]);
  const [numberNames, setNumberNames] = useState(() => {
    try { return JSON.parse(localStorage.getItem('excludeNumberNames') ?? '{}'); }
    catch { return {}; }
  });
  const [collpasedHour, setCollpasedHour] = useState(null);

  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [nextSelectedDayIndex, setNextSelectedDayIndex] = useState(0);
  const [addNumberInputVisible, setAddNumberInputVisible] = useState(false);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [isWatchDataModified, setIsWatchDataModified] = useState(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const defaultErrorMessage = t(DEFAULT_ERROR_TEXT);
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);

  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const defaultWarningMessageTitle = t("Warning! There has been errors in your profile, therefore the system saved the closest valid profile.");
  const [warningMessageTitle, setWarningMessageTitle] = useState(defaultWarningMessageTitle);
  const [warningMessageErrors, setWarningMessageErrors] = useState([]);

  const [shouldDisplayApplyModificationsPopup, setShouldDisplayApplyModificationsPopup] = useState(false);
  const [applyWatchSettingsDialogIsOpen, setApplyWatchSettingsDialogIsOpen] = useState(false);
  const [selectedDaysToApplyChanges, setSelectedDaysToApplyChanges] = useState([]);

  const dayNumberToTitle = {
    "0": t("Sunday"),
    "1": t("Monday"),
    "2": t("Tuesday"),
    "3": t("Wednesday"),
    "4": t("Thursday"),
    "5": t("Friday"),
    "6": t("Saturday")
  };

  useEffect(() => {
    if (typeof props.entityType === 'undefined') {
      return () => { };
    }

    let headers = {
      token: cookie.get('token')
    }

    let errorMessage = t(DEFAULT_ERROR_TEXT);

    let apiUrl = API_URL;
    if (props.entityType === "community") {
      apiUrl += `/getWatchProfile`;
    } else if (props.entityType === "customer") {
      apiUrl += `/by_customer/${props.customerId}/getWatchProfile`;
    } else if (props.entityType === "line") {
      apiUrl += `/by_line/${props.customerId}/${props.lineId}/getWatchProfile`;
    }

    let params = {
      service: WATCH_TYPE_CALL
    };
    if(watchType === WATCH_TYPE_SMS){
      params.service = WATCH_TYPE_SMS;
    }

    if (props.entityType === "community") {
      axios.get(API_URL + '/getWatchStatus', {headers, params})
      .then(
        ({ data }) => {
          if(data.hasOwnProperty("responseStatus") && (data['responseStatus'] === API_OK) && data.hasOwnProperty("status")){
            setWatchStatus(data.status);
          }else{
            if((data.responseStatus === API_EXCEPTION) && (data.hasOwnProperty("message"))){
              errorMessage = data["message"];
            }
            handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
          }
        }).catch((error) => {
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
      });
    }

    axios.get(apiUrl, { headers, params })
      .then(
        ({ data }) => {
          if (data.hasOwnProperty("responseStatus") && (data['responseStatus'] === API_OK)) {
            if (props.entityType !== "community") {
              setWatchStatus(data.status);
            }
            if (data.profile !== null) {
              setWatchProfile(data.profile);
            } else {
              setWatchProfile(initialWatchProfile);
            }
            if (data.exclude_numbers !== null) {
              setExcludeNumbers(data.exclude_numbers);
            }
          } else {
            if ((data.responseStatus === API_EXCEPTION) && (data.hasOwnProperty("message"))) {
              errorMessage = data["message"];
            }
            handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
          }
        }).catch((error) => {
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        });
  }, [props.entityType, watchType]);


  useEffect(() => {
    if(isWatchDataModified === true){
      handleSave();
    }
  }, [isWatchDataModified]);

  useEffect(() => {
    localStorage.setItem('excludeNumberNames', JSON.stringify(numberNames));
  }, [numberNames]);

  useEffect(() => {
    // Read the live community config from /getStat so a changed "smsWatchSystemAllow"
    // setting is reflected immediately (the login cookie can be stale).
    let cancelled = false;
    (async () => {
      const info = await getCommunityGeneralInfo(cookie.get('token'), t, dispatch, showErrorDialog, logout, navigate);
      if (!cancelled && info && info.hasOwnProperty('smsWatchSystemAllow')) {
        setDisplayWatchTypeSwitch(info.smsWatchSystemAllow === true || info.smsWatchSystemAllow === "true");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Convert user input → the international form stored/sent to the server (972…).
  //  *codes stay as-is; 972… kept; 0XXXXXXXXX → 972 + rest; anything else (mobile 55…,
  //  landline, or a 1700/1800 service number) → 972 + number (no leading 0 added).
  const toApiNumber = (n) => {
    if (n.startsWith('*')) return n;
    if (n.startsWith('972')) return n;
    if (n.startsWith('0')) return '972' + n.slice(1);
    return '972' + n;
  };
  // Convert the stored international form → what we show. Service numbers (local part
  //  starts with 1, e.g. 1700/1800) are shown without a leading 0; everything else gets 0.
  const toDisplayNumber = (n) => {
    if (n.startsWith('*')) return n;
    if (n.startsWith('972')) {
      const local = n.slice(3);
      return local.startsWith('1') ? local : '0' + local;
    }
    return n;
  };

  const handleDeleteExcludedNumber = (numberIndex) => {
    const num = excludeNumbers[numberIndex];
    const newExcludedNumbers = [...excludeNumbers];
    newExcludedNumbers.splice(numberIndex, 1);
    setExcludeNumbers(newExcludedNumbers);
    if (numberNames[num]) {
      const updated = { ...numberNames };
      delete updated[num];
      setNumberNames(updated);
    }
    setIsWatchDataModified(true);
  };

  const getExcludedNumbersChips = () => {
    if (excludeNumbers === null) return [];
    return excludeNumbers.map((number, numberIndex) => (
      <Chip
        label={toDisplayNumber(number)}
        sublabel={numberNames[number] ?? ''}
        key={number + numberIndex}
        onDelete={() => handleDeleteExcludedNumber(numberIndex)}
      />
    ));
  };

  const handleAddExcludedNumber = () => {
    const raw = document.getElementById('excluded_numbers').value.trim();
    if (raw === '') return;
    // strip spaces/dashes/parentheses; keep digits and a leading * (star service codes)
    const cleaned = raw.replace(/[^\d*]/g, '');
    const numericValueRegex = /^\*?\d+$/;
    if (!numericValueRegex.test(cleaned)) {
      alert(t('Please insert only numeric values'));
      return;
    }
    const apiNumber = toApiNumber(cleaned);
    const nameEl = document.getElementById('excluded_number_name');
    const nameValue = nameEl ? nameEl.value.trim() : '';
    if (!excludeNumbers.includes(apiNumber)) {
      setExcludeNumbers([...excludeNumbers, apiNumber]);
    }
    if (nameValue) {
      setNumberNames(prev => ({ ...prev, [apiNumber]: nameValue }));
    }
    document.getElementById('excluded_numbers').value = '';
    if (nameEl) nameEl.value = '';
    setIsWatchDataModified(true);
  };

  // Import excluded numbers from a CSV/text file (Excel → "Save As CSV"). Parsed client-side
  // (no dependency), normalized like a manually-typed number, then saved with the rest on Save.
  // A row may hold a name column alongside the number: any non-numeric cell(s) become the name,
  // the numeric cell becomes the number. Rows with no name (or files that are just numbers) are
  // imported without a name. Header rows (no numeric cell) are skipped automatically.
  const handleImportExcludedNumbers = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target.result || '').replace(/^﻿/, ''); // strip UTF-8 BOM
      const additions = [];
      const nameAdditions = {};
      const existing = new Set(excludeNumbers);

      const addOne = (numRaw, name) => {
        const apiNumber = toApiNumber(numRaw);
        if (existing.has(apiNumber) || additions.includes(apiNumber)) return;
        additions.push(apiNumber);
        if (name) nameAdditions[apiNumber] = name;
      };

      text.split(/\r?\n/).forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        // Split into columns by CSV delimiters; a delimiter-less line falls back to whitespace
        // (a plain list of numbers, or a "name number" line).
        const hasDelim = /[,;\t]/.test(trimmed);
        const cells = hasDelim
          ? trimmed.split(/[,;\t]/).map(s => s.trim()).filter(Boolean)
          : trimmed.split(/\s+/);

        const numberCells = [];
        const nameParts = [];
        cells.forEach((cell) => {
          const cleaned = cell.replace(/[^\d*]/g, '');
          const isNumber = /^\*?\d+$/.test(cleaned) && cleaned.replace(/\D/g, '').length >= 3;
          if (isNumber) numberCells.push(cleaned);
          else if (cell.trim()) nameParts.push(cell.trim());
        });
        if (numberCells.length === 0) return;       // header / non-numeric row → skip

        const name = nameParts.join(' ').trim();
        if (numberCells.length === 1) {
          addOne(numberCells[0], name);
        } else {
          // several numbers on one line → no reliable name-per-number mapping; keep the name for
          // the first only (relevant when the first column was a name in a delimited row).
          numberCells.forEach((num, i) => addOne(num, hasDelim && i === 0 ? name : ''));
        }
      });

      if (additions.length === 0) {
        alert(t('No valid numbers were found in the file'));
        return;
      }
      setExcludeNumbers([...excludeNumbers, ...additions]);
      setNumberNames(prev => ({ ...prev, ...nameAdditions }));
      setIsWatchDataModified(true);
    };
    reader.readAsText(file);
  };

  const displayFadingSuccessMessage = () => {
    setShowSuccessMessage(true);
    setTimeout(function (){
      setShowSuccessMessage(false);
  }, 2000);
  }

  const  handleSave = async () => {
    setLoaderVisible(true);

    setShowWarningMessage(false);
    setShowErrorMessage(false);

    let errorMessage = t(DEFAULT_ERROR_TEXT);

    let headers = {
      token: cookie.get('token')
    }

    let apiUrl = API_URL;

    let params = {
      service: WATCH_TYPE_CALL
    };
    
    if(watchType === WATCH_TYPE_SMS){
      params.service = WATCH_TYPE_SMS;
    }

    if(props.entityType === "community"){
      let isUpdateStatusSuccessful = false;
      let isUpdateProfileSuccessful = false;

      apiUrl = API_URL + '/setWatchStatus';
      params = { ...params, status: watchStatus, exclude_numbers: excludeNumbers };
      await axios.post(apiUrl, params, {headers})
        .then(
          ({ data }) => {
            if(data.hasOwnProperty("responseStatus") && (data['responseStatus'] === API_OK)){
              isUpdateStatusSuccessful = true;
            }else{
              if((data.responseStatus === API_EXCEPTION) && (data.hasOwnProperty("message"))){
                errorMessage = data["message"];
              }
              setErrorMessage(errorMessage);
              setShowErrorMessage(true);
              setIsWatchDataModified(false);
            }
          }).catch((error) => {
            setErrorMessage(errorMessage);
            setShowErrorMessage(true);
            setIsWatchDataModified(false);
        });

        apiUrl = API_URL + `/setWatchProfile`;

        params = { ...params, profile: watchProfile };

        await axios.put(apiUrl, params, { headers })
        .then(
          async (responseData) => {
            if (responseData.data.hasOwnProperty('responseStatus') && responseData.data.responseStatus === API_OK) {
              if (responseData.data.checkResponse.status === "OK") {
                setLoaderVisible(false);
                isUpdateProfileSuccessful = true;
              } else {
                if (responseData.data.checkResponse.status === "FIX") {
                  setWarningMessageErrors(responseData.data.checkResponse.problems);
                  setLoaderVisible(false);
                  setShowWarningMessage(true);
                }
                if (responseData.data.checkResponse.status === "MIS" || responseData.data.checkResponse.status === "ERROR") {
                  setLoaderVisible(false);
                  setShowErrorMessage(true);
                }
                setWatchProfile(responseData.data.checkResponse.profile);
              }
            } else {
              if ((responseData.data.responseStatus === API_EXCEPTION) && (responseData.data.hasOwnProperty("message"))) {
                errorMessage = responseData.data["message"];
              }
              setErrorMessage(errorMessage);
              setShowErrorMessage(true);
              setLoaderVisible(false);
            }
            setIsWatchDataModified(false);
          }).catch((error) => {
            setErrorMessage(errorMessage);
            setShowErrorMessage(true);
            setLoaderVisible(false);
            setIsWatchDataModified(false);
          });

          if(isUpdateStatusSuccessful && isUpdateProfileSuccessful){
            displayFadingSuccessMessage(true);
          }
    }else{
      params = { ...params, status: watchStatus, profile: watchProfile, exclude_numbers: excludeNumbers };
      apiUrl = API_URL;

      if (props.entityType === "customer") {
        apiUrl += `/by_customer/${props.customerId}/setWatchProfile`;
      } else if (props.entityType === "line") {
        apiUrl += `/by_line/${props.customerId}/${props.lineId}/setWatchProfile`;
      }

      await axios.put(apiUrl, params, { headers })
      .then(
        async (responseData) => {
          if (responseData.data.hasOwnProperty('responseStatus') && responseData.data.responseStatus === API_OK) {
            if (responseData.data.response.valid_profile_status === "OK") {
              setLoaderVisible(false);
              displayFadingSuccessMessage(true);
            } else {  
              if (responseData.data.response.valid_profile_status === "FIX") {
                setWarningMessageErrors(responseData.data.response.valid_profile_problems);
                setLoaderVisible(false);
                setShowWarningMessage(true);
              }
              if (responseData.data.response.valid_profile_status === "MIS" || responseData.data.response.valid_profile_status === "ERROR") {
                setLoaderVisible(false);
                setShowErrorMessage(true);
              }
              setWatchProfile(responseData.data.response.profile);
              setWatchStatus(responseData.data.response.status);
            }
          } else {
            if ((responseData.data.responseStatus === API_EXCEPTION) && (responseData.data.hasOwnProperty("message"))) {
              errorMessage = responseData.data["message"];
            }
            setErrorMessage(errorMessage);
            setShowErrorMessage(true);
            setLoaderVisible(false);
          }
          setIsWatchDataModified(false);
        }).catch((error) => {
          setErrorMessage(errorMessage);
          setShowErrorMessage(true);
          setLoaderVisible(false);
        });

    }
  }

  const formatHour = (hourIndex) => {
    if (hourIndex < 10) {
      return '0' + hourIndex + ':00';
    } else {
      return hourIndex + ':00';
    }
  }

  const changeWatchProfile = (value, id, modifiedDayIndex, modifiedHourIndex) => {
    setShowSuccessMessage(false);
    let newWatchProfile = Object.assign({}, watchProfile);
    newWatchProfile[modifiedDayIndex][modifiedHourIndex][id] = value;
    setWatchProfile(newWatchProfile);
    setIsWatchDataModified(true);
    setShouldDisplayApplyModificationsPopup(true);
  }

  const changeHourData = (newHourData, modifiedDayIndex, modifiedHourIndex) => {
    setShowSuccessMessage(false);
    let newWatchProfile = Object.assign({}, watchProfile);
    newWatchProfile[modifiedDayIndex][modifiedHourIndex] = newHourData;
    setWatchProfile(newWatchProfile);
    setIsWatchDataModified(true);
    setShouldDisplayApplyModificationsPopup(true);
  }

  const displayApplyPopupAndRememberNextSelectedDay = (dayIndex) => {
    setNextSelectedDayIndex(dayIndex);
    setApplyWatchSettingsDialogIsOpen(true);
    setShouldDisplayApplyModificationsPopup(false);
  }

  const getDayLabels = (letterOnly = false) => {
      let dayLabels = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        dayLabels.push(<div 
          key={'day'+dayIndex}
          className={'day'+(dayIndex === selectedDayIndex ? ' selected' : '')} 
          data-index={dayIndex}
          onClick={()=>{
            if(selectedDayIndex !== dayIndex
                && shouldDisplayApplyModificationsPopup === true){
                if(!selectedDaysToApplyChanges.includes(dayIndex)){
                  selectedDaysToApplyChanges.push(dayIndex);
                }
                displayApplyPopupAndRememberNextSelectedDay(dayIndex);
              }else{
                setSelectedDayIndex(dayIndex);
              }
          }}
          >{letterOnly ? t("Day Letter "+dayIndex) : t(dayNumberToTitle['' + dayIndex])}</div>)
      }
      return dayLabels;
  }

  const getDayLines = (dayIndex, hourIndexStart, hourIndexEnd, isAllCollpased = true) => {
    let dayLines = [];
    for (let hourIndex = hourIndexStart; hourIndex <= hourIndexEnd; hourIndex++) {
      dayLines.push(
        <div className="hour-wrapper" 
          key={'wrapper-day-hour-' + dayIndex + '-' + hourIndex}>
          <div
            onClick={(e) => {
              if(collpasedHour !== hourIndex){
                setCollpasedHour(hourIndex);
                e.stopPropagation();
              }else{
                setCollpasedHour(null);
              }
            }}
            >{formatHour(hourIndex)}</div>
          {(
            isAllCollpased === true || collpasedHour === hourIndex ?
            <>
            <Select values={{
                [ALL]: t("All"),
                [WATCH_STATUS_CALLS_OUT]: t("Watch outcoming calls"),
                [WATCH_STATUS_CALLS_IN]: t("Watch incoming calls")
              }} 
                value={watchProfile === null ? ALL : watchProfile[dayIndex][hourIndex]['calls']}
                handleClick={(newValue, e) => {
                  changeWatchProfile(newValue, "calls", dayIndex, hourIndex);
                  e.stopPropagation();
                }}
                />
            <Switch 
              type="text"
              name="status"
              values={{
                [WATCH_STATUS_CLOSED]: t("Watch Inactive"),
                [WATCH_STATUS_OPEN]: t("Watch Active") 
              }}
              value={watchProfile === null ? WATCH_STATUS_OPEN : watchProfile[dayIndex][hourIndex]['status']}
              mainValue={WATCH_STATUS_OPEN}
              handleClick={(newValue,e ) => {
                changeWatchProfile(newValue, "status", dayIndex, hourIndex);
                e.stopPropagation();
              }}
              />
            </>
             : ''
          )}
        </div>);
    }
    return dayLines;
  }

  const warningMessageErrorLines = () => {
    let errorLines = [];
    for (let errorIndex = 0; errorIndex < warningMessageErrors.length; errorIndex++) {
      errorLines.push(<li className='warning-message-error' key={'warning-message-error-' + errorIndex}>{warningMessageErrors[errorIndex]}</li>);
    }
    return errorLines;
  };

  const getApplyDays = () => {
    let applyDays = [];
    for(let index = 0; index < 7; index++){
      applyDays.push(<div 
        key={'apply-day-'+index}
        className={'day'+(selectedDaysToApplyChanges.includes(index) || index === selectedDayIndex ? ' selected':'')}
        onClick={()=>{
          if(selectedDaysToApplyChanges.includes(index) && index !== selectedDayIndex){
            var indexKey = selectedDaysToApplyChanges.indexOf(index);
            if (indexKey !== -1) {
              let newSelectedDaysToApplyChanges = JSON.parse(JSON.stringify(selectedDaysToApplyChanges));
              newSelectedDaysToApplyChanges.splice(indexKey, 1);
              setSelectedDaysToApplyChanges(newSelectedDaysToApplyChanges);
            }
          }else{
            setSelectedDaysToApplyChanges([...selectedDaysToApplyChanges, index]);
          }
        }}
      >
        {t('Day Letter '+index)}
      </div>);
    }
    return applyDays
  }

  const applyProfileChangesOnSelectedDays = () => {
    if(selectedDaysToApplyChanges.length > 0){
      let newWatchProfile = watchProfile;
      for(let index = 0; index < 7; index++){
        if(selectedDaysToApplyChanges.includes(index)){
          newWatchProfile[index] = {...watchProfile[selectedDayIndex]};
        }
      }
      setWatchProfile(newWatchProfile);
      setIsWatchDataModified(true);
      setApplyWatchSettingsDialogIsOpen(false);
      setSelectedDaysToApplyChanges([]);
      setSelectedDayIndex(nextSelectedDayIndex);
    }
  }

  return (<>
      <div className={'watch-wrapper '+(props.isDesktop === true ? ' desktop' : ' mobile')+(props.watchStatus === WATCH_STATUS_ON ? ' on' : ' off')}
        onClick={(e) => {
          setCollpasedHour(null);
        }}
      >
        {(
          props.isDesktop ? 
          <WatchSystemDesktop
            isCommunityWide={!props.customerId}
            saving={loaderVisible}
            showSuccessMessage={showSuccessMessage}
            errorMessage={errorMessage}
            showErrorMessage={showErrorMessage}
            setShowErrorMessage={setShowErrorMessage}
            warningMessageErrorLines={warningMessageErrorLines}
            showWarningMessage={showWarningMessage}
            setShowWarningMessage={setShowWarningMessage}
            warningMessageTitle={warningMessageTitle}
            setWatchStatus={setWatchStatus}
            watchStatus={watchStatus}
            setWatchType={setWatchType}
            watchType={watchType}
            displayWatchTypeSwitch={displayWatchTypeSwitch}
            setIsWatchDataModified={setIsWatchDataModified}
            getDayLabels={getDayLabels}
            getExcludedNumbersChips={getExcludedNumbersChips}
            addNumberInputVisible={addNumberInputVisible}
            setAddNumberInputVisible={setAddNumberInputVisible}
            handleAddExcludedNumber={handleAddExcludedNumber}
            handleImportExcludedNumbers={handleImportExcludedNumbers}
            applyWatchSettingsDialogIsOpen={applyWatchSettingsDialogIsOpen}
            getApplyDays={getApplyDays}
            applyProfileChangesOnSelectedDays={applyProfileChangesOnSelectedDays}
            setApplyWatchSettingsDialogIsOpen={setApplyWatchSettingsDialogIsOpen}
            selectedDayIndex={selectedDayIndex}
            nextSelectedDayIndex={nextSelectedDayIndex}
            setSelectedDayIndex={setSelectedDayIndex}
            getDayLines={getDayLines}
            watchProfile={watchProfile}
            changeWatchProfile={changeWatchProfile}
            changeHourData={changeHourData}
          /> :
          <WatchSystemMobile 
            showSuccessMessage={showSuccessMessage}
            errorMessage={errorMessage}
            showErrorMessage={showErrorMessage}
            setShowErrorMessage={setShowErrorMessage}
            warningMessageErrorLines={warningMessageErrorLines}
            showWarningMessage={showWarningMessage}
            setShowWarningMessage={setShowWarningMessage}
            warningMessageTitle={warningMessageTitle}
            setWatchStatus={setWatchStatus}
            watchStatus={watchStatus}
            setWatchType={setWatchType}
            watchType={watchType}
            displayWatchTypeSwitch={displayWatchTypeSwitch}
            setIsWatchDataModified={setIsWatchDataModified}
            getDayLabels={getDayLabels}
            getExcludedNumbersChips={getExcludedNumbersChips}
            addNumberInputVisible={addNumberInputVisible}
            setAddNumberInputVisible={setAddNumberInputVisible}
            handleAddExcludedNumber={handleAddExcludedNumber}
            handleImportExcludedNumbers={handleImportExcludedNumbers}
            applyWatchSettingsDialogIsOpen={applyWatchSettingsDialogIsOpen}
            getApplyDays={getApplyDays}
            applyProfileChangesOnSelectedDays={applyProfileChangesOnSelectedDays}
            setApplyWatchSettingsDialogIsOpen={setApplyWatchSettingsDialogIsOpen}
            selectedDayIndex={selectedDayIndex}
            nextSelectedDayIndex={nextSelectedDayIndex}
            setSelectedDayIndex={setSelectedDayIndex}
            getDayLines={getDayLines}
            setCustomerView={props.setCustomerView}
          />
        )}
      </div>
  </>
  );
}
